import React from 'react';
import PropTypes from 'prop-types';
import Sparkline from '../../../../components/Sparkline/Sparkline';
import { EM_DASH, formatNumber } from './queueMonitorFormat';

/**
 * CapacityTrend (v3 contract 5.2 + 5.3)
 *
 * 30/90-day capacity view fed by GET /api/violerts-queue/capacity:
 *   {
 *     window_days: 90, basis: "job_metrics_daily",
 *     points: [ { date, attempts, failures, failure_rate } ],
 *     summary: { attempts_trend_pct_30d, failure_rate_trend_pct_30d,
 *                peak_day: { date, attempts } }
 *   }
 *
 * Renders attempts (throughput proxy) and failure-rate daily sparklines, the
 * 30-day trend deltas as signed colored chips, and the peak day. Boss-reportable,
 * same visual language as the WoW card.
 *
 * Locked behaviors (v3 contract 5.3):
 * - 30/90-day toggle; onDaysChange drives the page fetch.
 * - data null (loading / pre-v3 backend) -> renders the shell + toggle with an
 *   honest empty message (never a fabricated trend).
 * - points empty -> "no capacity data yet".
 * - failure_rate is the canonical attempt-level rate (null below floor -> em dash,
 *   never 0%); rendered as percent.
 * - trend chips: positive attempts delta is neutral-good (more throughput),
 *   positive failure-rate delta is bad (more failures). null -> em dash chip.
 * - "since v2 deploy" label on per-queue slices (capacity totals are all-queue
 *   and valid across the full window - this view is all-queue, so the label is
 *   shown only when the backend marks a per-queue slice, which this all-queue
 *   view does not; the basis caption states the source).
 */

const DAY_OPTIONS = [
	{ days: 30, label: '30 days' },
	{ days: 90, label: '90 days' },
];

const formatRatePct = (rate) => {
	if (rate === null || rate === undefined || !Number.isFinite(rate)) {
		return EM_DASH;
	}
	return `${(rate * 100).toFixed(2)}%`;
};

// Signed percentage chip. `goodWhenNegative` flips the color semantics:
// for failure-rate trend a drop is good; for attempts a rise is good.
const TrendChip = ({ label, pct, goodWhenNegative }) => {
	if (pct === null || pct === undefined || !Number.isFinite(pct)) {
		return (
			<span className="capacity-chip text-muted me-3">
				{label}: {EM_DASH}
			</span>
		);
	}
	const isFlat = pct === 0;
	const isGood = isFlat ? null : goodWhenNegative ? pct < 0 : pct > 0;
	const color = isGood === null ? '#6c757d' : isGood ? '#28a745' : '#dc3545';
	const sign = pct > 0 ? '+' : '';
	return (
		<span className="capacity-chip me-3" style={{ color }}>
			{label}: {sign}
			{pct}%
		</span>
	);
};

TrendChip.propTypes = {
	label: PropTypes.string.isRequired,
	pct: PropTypes.number,
	goodWhenNegative: PropTypes.bool,
};

TrendChip.defaultProps = {
	pct: null,
	goodWhenNegative: false,
};

const CapacityTrend = ({ data, windowDays, onDaysChange, isLoading }) => {
	const toggle = (
		<div
			className="btn-group btn-group-sm capacity-day-selector"
			role="group"
			aria-label="Capacity window"
		>
			{DAY_OPTIONS.map((option) => (
				<button
					key={option.days}
					type="button"
					className={`btn ${option.days === windowDays ? 'btn-primary' : 'btn-outline-primary'}`}
					onClick={() => onDaysChange(option.days)}
				>
					{option.label}
				</button>
			))}
		</div>
	);

	const header = (
		<div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
			<h5 className="mb-0 me-2">Capacity trend</h5>
			{toggle}
		</div>
	);

	if (!data) {
		// Loading / pre-v3 backend: shell + toggle, honest empty message.
		return (
			<div className="analytics-card capacity-trend" aria-busy={!!isLoading}>
				{header}
				<p className="text-muted capacity-empty mb-1">
					{isLoading ? 'Loading capacity trend...' : 'No capacity data yet'}
				</p>
			</div>
		);
	}

	const points = Array.isArray(data.points) ? data.points : [];
	const attemptsSeries = points
		.map((p) => (typeof p.attempts === 'number' ? p.attempts : null))
		.filter((v) => typeof v === 'number');
	const rateSeries = points
		.map((p) => (typeof p.failure_rate === 'number' ? p.failure_rate : null))
		.filter((v) => typeof v === 'number');

	const summary = data.summary || {};
	const peakDay = summary.peak_day || null;
	const latest = points.length > 0 ? points[points.length - 1] : null;

	return (
		<div
			className="analytics-card capacity-trend"
			aria-busy={!!isLoading}
		>
			{header}

			{points.length === 0 ? (
				<p className="text-muted capacity-empty mb-1">No capacity data yet</p>
			) : (
				<React.Fragment>
					<div className="capacity-trends-row mb-2">
						<TrendChip
							label="Throughput 30d"
							pct={
								typeof summary.attempts_trend_pct_30d === 'number'
									? summary.attempts_trend_pct_30d
									: null
							}
							goodWhenNegative={false}
						/>
						<TrendChip
							label="Failure rate 30d"
							pct={
								typeof summary.failure_rate_trend_pct_30d === 'number'
									? summary.failure_rate_trend_pct_30d
									: null
							}
							goodWhenNegative
						/>
						{peakDay && peakDay.date && (
							<span className="capacity-peak text-muted me-3">
								Peak: {formatNumber(peakDay.attempts)} on {peakDay.date}
							</span>
						)}
					</div>

					<div className="row g-3">
						<div className="col-12 col-lg-6 capacity-attempts">
							<div className="text-muted small mb-1">
								Daily throughput (attempts)
							</div>
							<Sparkline
								points={attemptsSeries}
								width={300}
								height={56}
								stroke="#0d6efd"
								ariaLabel="daily attempts trend"
							/>
							<div className="small mt-1">
								Latest:{' '}
								{latest ? formatNumber(latest.attempts) : EM_DASH}
							</div>
						</div>
						<div className="col-12 col-lg-6 capacity-failure-rate">
							<div className="text-muted small mb-1">
								Daily failure rate
							</div>
							<Sparkline
								points={rateSeries}
								width={300}
								height={56}
								stroke="#dc3545"
								ariaLabel="daily failure rate trend"
							/>
							<div className="small mt-1">
								Latest:{' '}
								{latest ? formatRatePct(latest.failure_rate) : EM_DASH}
							</div>
						</div>
					</div>
				</React.Fragment>
			)}

			<div className="text-muted small mt-2">
				{data.basis === 'job_metrics_daily'
					? 'daily grain, attempt-level (job_metrics)'
					: `basis ${data.basis || 'unknown'}`}
			</div>
		</div>
	);
};

CapacityTrend.propTypes = {
	// /capacity payload; null = loading/pre-v3 backend (renders shell + empty).
	data: PropTypes.object,
	windowDays: PropTypes.number.isRequired,
	onDaysChange: PropTypes.func.isRequired,
	isLoading: PropTypes.bool,
};

CapacityTrend.defaultProps = {
	data: null,
	isLoading: false,
};

export default CapacityTrend;
